/**
 *
 * @description APPLE PAY 支付相关配置
 * @date 2024/01/03 15:31:33
 */
import { armsErrorSend } from '@/utils/libs/armsErrorSend';
import { throttle } from 'lodash';
import { addSearchParamsWithoutEncode } from '../url';
import { httpRequest } from '@/request';
import { SERVER } from '@/request/config';
import { removeDoms } from '../dom';

type ClientIDConfigType = {
  [key: string]: { sandbox: string; live: string };
};

// 美国和其他地区默认配置一致，单独拆出来方便后续美国主体切换直接更换
// paypal client id
export const ClientIDConfig: ClientIDConfigType = {
  US: {
    sandbox: 'ARgqwXjf34lIT8m9qSPcohbGbGWRRIPfj4nicPDoG_8Od-LT9_gcrDx8VDEaLMJHEn-ralp2pEYc_X3v',
    live: 'AfRa7ckm3rNfe-d7zv6tNpxe7ZDfZpirDtg0alBMwlr3h0zbD8vHfvcPcobWaGQvVKMomTlVamJgvajT',
  },
  default: {
    sandbox: 'Ae14VGc-f1gE8vrrJzVXPNtcG9AdFCdBYONOFQ9ebaDf1thGIZS69ufz4HITvA7lZcbC_EP_bXh58u8b',
    live: 'AcgvoR8L0yQNUq4omycSdIvEpL7wIWpaZReAQI7ctAGcbFNu8YG-zlkNE1OnxAexpv2zY8kaH2SevyD0',
  },
};

export const getClientId = (code: string, isProd: boolean) => {
  const config = ClientIDConfig[code] || ClientIDConfig.default;
  return isProd ? config.live : config.sandbox;
};

// getPaypalIdToken接口返回值，为了前后创建订单 / 请求SDK的参数一直，返回locale
type PayIdTokenResponse = {
  /**  */
  idToken?: string;
  /** 当前message / 支付弹窗的展示语言 */
  locale: string;
};

/** 获取paypal id token，用于初始化SDK */
const getPaypalIdToken = (): Promise<void | PayIdTokenResponse | undefined> => {
  return httpRequest<PayIdTokenResponse>({
    url: SERVER.API_SERVER_PAYMENT + '/paypal/getIdToken',
    instaToken: true,
  }).then((res) => {
    if (res?.code === 0) {
      return res.data;
    }
    return undefined;
  });
};

type LoadSdkPublicProps = {
  vault: boolean;
  successCb?: () => void;
  failCb?: (payload: void | string | Event) => void;
};

type LoadSdkProps = {
  idToken: string | undefined;
  /** 语言类型，和后端保持一致，可以不返回sdk自动推断 */
  locale?: string;
} & LoadSdkPublicProps;

/**
 * 下载PsbSdk
 * @param {string} idToken 只有paypal订阅用户才有，其他用户不存在这个id token
 * @param {boolean} vault 当前商品中是否有云存储商品 订阅模式下载参数不同
 * @param {Function} successCb
 * @param {Function} failCb
 */
const loadSdk = ({ idToken, locale: _locale, vault, successCb, failCb }: LoadSdkProps) => {
  // 清除之前的script
  removeDoms("script[data-page-type='checkout']");

  const { currency, code } = window.country;
  const scriptElement = document.createElement('script');
  const locale = _locale;
  const clientId = getClientId(code, window.isProd);

  const src = addSearchParamsWithoutEncode({
    url: 'https://www.paypal.com/sdk/js',
    params: {
      'client-id': clientId,
      currency,
      'vault': `${vault}`,
      'intent': 'capture',
      'locale': locale || '',
      'components': 'buttons,messages',
      'enable-funding': 'paylater,credit',
      'disable-funding': 'card,sepa,bancontact,eps,giropay,ideal,mybank,p24,sofort,blik,mercadopago',
      'buyer-country': window.isProd ? '' : code,
    },
  });

  scriptElement.src = src;
  scriptElement.setAttribute('data-page-type', 'checkout');
  if (idToken) scriptElement.setAttribute('data-user-id-token', idToken);
  scriptElement.onload = () => {
    successCb?.();
  };

  scriptElement.onerror = (e) => {
    failCb?.(e);
  };

  document.body.appendChild(scriptElement);
};

/**
 * 下载PSB sdk 对外暴露的通用下载方式
 * 内部有超时限制，限制10s
 * @param vault 当前商品中是否有订阅商品
 * @param successCb
 * @param failCb
 */
export const loadSPBSdk = throttle(async (props: LoadSdkPublicProps) => {
  const { successCb, failCb } = props;

  try {
    // getPaypalIdToken接口可能调用失败，回退failCb
    const { idToken, locale } = (await getPaypalIdToken()) || {};
    let timeoutTimer: NodeJS.Timeout | undefined = undefined;
    // 当excuting=false，表示这个竞争请求已经结束，不应当再执行任何的sdk success / fail回调方法
    let excuting = true;
    const timeoutPromise = new Promise((resolve, reject) => {
      timeoutTimer = setTimeout(() => {
        reject('PSB timeout');
      }, 10 * 1000);
    });
    const loadSdkPromise = new Promise((resolve, reject) => {
      const successResolve = () => {
        excuting && successCb?.();
        resolve(true);
      };

      const failReject = (e: Event | void | string) => {
        excuting && failCb?.();
        reject(e);
      };

      loadSdk({
        idToken,
        locale,
        ...props,
        successCb: successResolve,
        failCb: failReject,
      });
    });

    Promise.race([timeoutPromise, loadSdkPromise])
      .finally(() => {
        clearTimeout(timeoutTimer);
        excuting = false;
      })
      .catch((e) => {
        failCb?.();
        armsErrorSend(e, {
          filename: 'paypal buttons：下载SDK失败',
        });
      });
  } catch (e) {
    failCb?.();
    armsErrorSend(e as Error, {
      filename: 'paypal buttons：loadSPBSdk 下载SDK失败',
    });
  }
}, 500);

/**
 * 获取SDK下载时的语言locale - 从后端获取，此方法暂时不使用，
 * 数值查看https://arashivision.feishu.cn/wiki/OjvKwn8Xuif6cAka7KUcLwojnwe */
export const getPSBLocale = (countryCode: string) => {
  if (countryCode === 'MO') {
    return 'zh_HK';
  }
  return (window.country && window.country.language.split('_')[0] + '_' + countryCode) ?? '';
};
